import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from 'react-intl';

import { Table, Popconfirm, Checkbox } from 'antd';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { hideModal } from 'redux/modal/actions';
import {
  fetchWinnerGiftsStart,
  claimWinnerGiftStart,
  setWinnerState,
  reloadPage,
} from 'redux/winners/actions';

import ModalTypes from 'constants/modalTypes';

import moment from 'moment';

import styles from './style.module.scss';

const GiftDetails = ({ currentModals, winnerId }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { winnerGifts, isFetchingWinnerGifts, isClaimingGift, hasClaimedGift } = useSelector(
    state => state.winner,
  );

  const [isPopoverVisible, setPopoverVisible] = useState(false);
  const [popoverIndex, setPopoverIndex] = useState(null);

  const columns = [
    {
      title: '',
      width: 70,
      dataIndex: 'image',
      key: 'image',
      render: (_, record) =>
        record.gift.image && (
          <img className={styles.giftImage} src={record.gift.image} alt='Gift' />
        ),
    },
    {
      title: intl.formatMessage({ id: 'winners.gifts.table.headers.title' }),
      dataIndex: 'title',
      key: 'title',
      elipsis: true,
      render: (_, record) => (record.gift && record.gift.title) || '',
    },
    {
      title: intl.formatMessage({ id: 'winners.gifts.table.headers.dateWon' }),
      width: 120,
      dataIndex: 'dateWon',
      key: 'dateWon',
      render: value => moment(value.toDate()).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'winners.gifts.table.headers.claimed' }),
      dataIndex: 'isClaimed',
      key: 'isClaimed',
      width: 80,
      align: 'center',
      render: (value, record, rowIndex) =>
        winnerGifts.length > 0 && (
          <Popconfirm
            title={
              value
                ? intl.formatMessage({ id: 'winners.gifts.table.popover.unclaim' })
                : intl.formatMessage({ id: 'winners.gifts.table.popover.claim' })
            }
            onConfirm={() => handleClaimChange(!value, rowIndex)}
            onCancel={() => {
              setPopoverVisible(false);
              setPopoverIndex(null);
            }}
            okText={intl.formatMessage({ id: 'winners.gifts.table.popover.okText' })}
            cancelText={intl.formatMessage({ id: 'winners.gifts.table.popover.cancelText' })}
            destroyTooltipOnHide
            visible={isPopoverVisible && popoverIndex === rowIndex}
            okButtonProps={{
              loading: isClaimingGift,
              className: styles.confirmBtn,
            }}
          >
            <Checkbox
              checked={value}
              size='large'
              onClick={e => {
                e.stopPropagation();
                setPopoverIndex(rowIndex);
                setPopoverVisible(!isPopoverVisible);
              }}
            />
          </Popconfirm>
        ),
    },
  ];

  useEffect(() => {
    dispatch(fetchWinnerGiftsStart(winnerId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasClaimedGift) {
      dispatch(setWinnerState({ hasClaimedGift: false }));
      dispatch(reloadPage());
      setPopoverIndex(null);
      setPopoverVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasClaimedGift]);

  const handleClaimChange = (isClaimed, rowIndex) => {
    dispatch(claimWinnerGiftStart(rowIndex, isClaimed, winnerId));
  };

  const handleToggle = () => {
    dispatch(hideModal(ModalTypes.WINNER_GIFTS));
  };

  const giftsMemo = useMemo(() => {
    return winnerGifts;
  }, [winnerGifts]);

  const handleOnExit = () => {
    dispatch(setWinnerState({ winnerGifts: [] }));
  };

  return (
    <Modal
      isOpen={Object.prototype.hasOwnProperty.call(currentModals, ModalTypes.WINNER_GIFTS)}
      toggle={handleToggle}
      centered
      scrollable
      unmountOnClose
      onExit={handleOnExit}
    >
      <ModalHeader toggle={handleToggle}>
        {intl.formatMessage({ id: 'winners.gifts.title' })}
      </ModalHeader>
      <ModalBody>
        <Table
          tableLayout='auto'
          columns={columns}
          rowKey='dateWon'
          dataSource={giftsMemo || []}
          loading={isFetchingWinnerGifts}
          pagination={false}
          scroll={{ y: '100vh', x: true }}
          locale={{ emptyText: intl.formatMessage({ id: 'winners.gifts.table.emptyText' }) }}
          rowClassName='table-row'
        />
      </ModalBody>
    </Modal>
  );
};

export default GiftDetails;
